import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box,
  FormControlLabel,
  Checkbox 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';





const AddTemplateDialog = ({ open, onClose, onSave, isSuperAdmin }) => {
  const [templateName, setTemplateName] = useState(''); // State for template name
  const [columns, setColumns] = useState(['Column 1', 'Withdrawn']); // Initial columns including Withdrawn
  const riskManagementColumns = [
    'Risk Identification',
    'Analysis',
    'Mitigation Planning',
    'In-Progress',
    'Monitoring',
    'Done',
    'Blocked',
    'Withdrawn',
  ];

  const issueManagementColumns = [
    'New',
    'Triage',
    'Open',
    'Resolved',
    'Reopen',
    'Rejected', 
    'Verification',
    'Closed',
    'Blocked',
    'Withdrawn'
  ];

  const crmColumns = [
    'Prospecting',
    'Qualified',
    'Proposal Sent',
    'Negotiation',
    'Won',
    'Lost',
    'Withdrawn'
  ];
  const [makeGlobal, setMakeGlobal] = useState(false);

  const fieldSx = {
    '& .MuiInputLabel-root': {
      color: '#5f6b7a',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#1f4fbf',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#f8fbff',
      '& fieldset': {
        borderColor: 'rgba(15, 23, 42, 0.14)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(31, 79, 191, 0.35)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1f4fbf',
        boxShadow: '0 0 0 3px rgba(31, 79, 191, 0.10)',
      },
    },
  };

  const presetButtonSx = {
    minHeight: 42,
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 700,
    px: 1.75,
    py: 0.8,
    justifyContent: 'center',
    whiteSpace: 'nowrap',
  };

  // This Resets the state when dialog opens
  useEffect(() => {
    if (open) {
      setTemplateName('');
      setColumns(['Column 1']);
      setMakeGlobal(false);
    }
  }, [open]);

  // If user selects Risk Management Template, prepopulate columns
  const handleRiskTemplateSelect = () => {
    setTemplateName('Risk Management');
    setColumns(riskManagementColumns);
  };

  // If user selects Issue Management Template, prepopulate columns
  const handleIssueTemplateSelect = () => {
    setTemplateName('Issue Management');
    setColumns(issueManagementColumns);
  };

  // If user selects CRM Template, prepopulate columns
  const handleCRMTemplateSelect = () => {
    setTemplateName('CRM');
    setColumns(crmColumns);
  };

  
  // Handle adding a new column
  // const handleAddColumn = () => {
  //   setColumns([...columns, `Column ${columns.length + 1}`]);
  // };
  const handleAddColumn = () => {
    setColumns([...columns, `Column ${columns.length + 1}`]);
  };

  // Handle input change for columns
  const handleColumnInputChange = (index, value) => {
    const updatedColumns = [...columns];
    updatedColumns[index] = value;
    setColumns(updatedColumns);
  };

  // Handle input change for templateName
  const handleTemplateNameChange = (e) => {
    setTemplateName(e.target.value);
  };

  // Save template and columns to the backend
  const handleSaveTemplate = async () => {
    const templateData = {
      templateName, // Use the value of the templateName state
      columns,
      createdAt: new Date(),
      makeGlobal,
    };

    try {
      await onSave(templateData); // Pass the template data to onSave function passed as prop
      console.log('Template saved successfully!');
      onClose(); // Close dialog after saving
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          border: '1px solid rgba(148, 163, 184, 0.22)',
          boxShadow: '0 22px 60px rgba(15, 23, 42, 0.22)',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 1,
          fontSize: { xs: '1.8rem', sm: '2rem' },
          fontWeight: 800,
          letterSpacing: '-0.02em',
          fontFamily: '"Segoe UI", "Poppins", "Inter", sans-serif',
          color: '#0f172a',
        }}
      >
        Add Template
      </DialogTitle>
      <DialogContent sx={{ pt: 1, px: 3, pb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.25,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleRiskTemplateSelect}
            sx={{
              ...presetButtonSx,
              color: '#2563eb',
              borderColor: '#bcd1fb',
              backgroundColor: 'rgba(255,255,255,0.72)',
              '&:hover': { borderColor: '#7ea6ea', backgroundColor: '#ffffff' },
            }}
          >
            Use Risk Management Template
          </Button>
          <Button
            variant="outlined"
            onClick={handleIssueTemplateSelect}
            sx={{
              ...presetButtonSx,
              color: '#9333ea',
              borderColor: '#d9b8fb',
              backgroundColor: 'rgba(255,255,255,0.72)',
              '&:hover': { borderColor: '#c084fc', backgroundColor: '#ffffff' },
            }}
          >
            Use Issue Management Template
          </Button>
          <Button
            variant="outlined"
            onClick={handleCRMTemplateSelect}
            sx={{
              ...presetButtonSx,
              gridColumn: { xs: 'auto', sm: '1 / -1' },
              justifySelf: { xs: 'stretch', sm: 'center' },
              width: { xs: '100%', sm: '60%' },
              color: '#2e7d32',
              borderColor: '#b7d7ba',
              backgroundColor: 'rgba(255,255,255,0.72)',
              '&:hover': { borderColor: '#81c784', backgroundColor: '#ffffff' },
            }}
          >
            Use CRM Template
          </Button>
        </Box>
        {/* Template Name TextField */}
        <TextField
          fullWidth
          margin="normal"
          label="Template Name"
          value={templateName}
          onChange={handleTemplateNameChange}
          sx={fieldSx}
        />
        {/* Columns input fields */}
        <Box sx={{ mt: 0.5 }}>
          {columns.map((column, index) => (
            <TextField
              key={index}
              fullWidth
              margin="normal"
              label={`Column ${index + 1}`}
              value={column}
              onChange={(e) => handleColumnInputChange(index, e.target.value)}
              sx={fieldSx}
            />
          ))}
        </Box>
        {/* Add Column Button */}
        <Box sx={{ mt: 1, mb: 1 }}>
          <Button
            onClick={handleAddColumn}
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              color: '#2563eb',
              px: 1.25,
              py: 0.75,
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
              },
            }}
          >
            Add Column
          </Button>
        </Box>
        <Box sx={{ mt: 0.5 }}>
        {isSuperAdmin && (
          <FormControlLabel
            sx={{
              mx: 0,
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(148, 163, 184, 0.2)',
            }}
            control={
              <Checkbox
                checked={makeGlobal}
                onChange={e => setMakeGlobal(e.target.checked)}
                color="primary"
              />
            }
            label="Make Global (available to all tenants)"
          />
        )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 0, pt: 1, pb: 0 }}>
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={1.5}
          width="100%"
          p={2}
          sx={{
            borderTop: '1px solid rgba(148, 163, 184, 0.18)',
            background: 'rgba(255,255,255,0.75)',
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              px: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#9333ea',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 10px 20px rgba(37, 99, 235, 0.22)',
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              },
            }}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddTemplateDialog;
